import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  getFighterDashboard, 
  getFighterSubscriptionStatus,
  createFighterSubscriptionCheckout,
  searchGyms,
  requestToJoinGym,
  createTrainingLog,
  getFightWeekData,
  logFightWeekWeighIn,
  updateFightWeekChecklist,
  getFighterCampGoals,
  updateCampGoal,
  trackFeatureUsage 
} from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { BeltTrackingCard } from '../components/BeltTracking';
import { 
  User, 
  Swords, 
  MessageSquare,
  Dumbbell,
  Crown,
  Search,
  Send,
  ChevronRight,
  Target,
  Settings,
  LogOut,
  Clock,
  Calendar,
  Building2,
  Plus,
  Trophy,
  TrendingUp,
  X,
  Scale,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Flame,
  Timer,
  Goal,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { differenceInDays, isValid, parseISO, format } from 'date-fns';
import { cn } from '../lib/utils';

// Safe date helpers
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
  return isValid(date) ? date : null;
};

const safeDaysUntil = (dateStr) => {
  const date = safeParseDate(dateStr);
  if (!date) return null;
  return differenceInDays(date, new Date());
};

const safeDateFormat = (dateStr, formatStr) => {
  const date = safeParseDate(dateStr);
  if (!date) return 'TBD';
  return format(date, formatStr);
};

const FighterDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Join gym dialog
  const [showJoinGymDialog, setShowJoinGymDialog] = useState(false);
  const [gymSearch, setGymSearch] = useState('');
  const [gymsResults, setGymsResults] = useState([]);
  const [searchingGyms, setSearchingGyms] = useState(false);
  const [selectedGym, setSelectedGym] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Workout log dialog
  const [showLogWorkout, setShowLogWorkout] = useState(false);
  const [workoutData, setWorkoutData] = useState({
    focus: '',
    coach: '',
    duration: '60'
  });
  const [savingWorkout, setSavingWorkout] = useState(false);
  
  // Fight Week state
  const [fightWeekData, setFightWeekData] = useState(null);
  const [showWeighInDialog, setShowWeighInDialog] = useState(false);
  const [weighInWeight, setWeighInWeight] = useState('');
  const [savingWeighIn, setSavingWeighIn] = useState(false);
  
  // Camp Goals state
  const [campGoalsData, setCampGoalsData] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [dashboardRes, subRes, fightWeekRes, goalsRes] = await Promise.all([
        getFighterDashboard(),
        getFighterSubscriptionStatus(),
        getFightWeekData(),
        getFighterCampGoals()
      ]);
      setDashboardData(dashboardRes.data);
      setSubscriptionStatus(subRes.data);
      setFightWeekData(fightWeekRes.data);
      setCampGoalsData(goalsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'fighter') {
      navigate('/dashboard');
      return;
    }
    loadData();
    trackFeatureUsage('fighter_dashboard', 'view').catch(() => {});
  }, [user, navigate, loadData]);

  const handleSearchGyms = async () => {
    if (!gymSearch.trim()) return;
    setSearchingGyms(true);
    try {
      const res = await searchGyms(gymSearch);
      setGymsResults(res.data);
    } catch (error) {
      toast.error('Failed to search gyms');
    } finally {
      setSearchingGyms(false);
    }
  };

  const handleRequestJoinGym = async () => {
    if (!selectedGym) return;
    setSendingRequest(true);
    try {
      await requestToJoinGym({
        gym_id: selectedGym.id,
        message: joinMessage
      });
      toast.success('Join request sent!');
      setShowJoinGymDialog(false);
      setSelectedGym(null);
      setJoinMessage('');
      setGymsResults([]);
      setGymSearch('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send request'));
    } finally {
      setSendingRequest(false);
    }
  };

  const handleLogWorkout = async () => {
    if (!workoutData.focus.trim()) {
      toast.error('Please enter what you worked on');
      return;
    }
    setSavingWorkout(true);
    try {
      await createTrainingLog({
        date: new Date().toISOString().split('T')[0],
        title: workoutData.focus,
        description: workoutData.coach ? `Coach: ${workoutData.coach}` : '',
        duration_minutes: parseInt(workoutData.duration) || 60,
        training_type: 'training'
      });
      toast.success('Workout logged!');
      setShowLogWorkout(false);
      setWorkoutData({ focus: '', coach: '', duration: '60' });
      loadData();
    } catch (error) {
      toast.error('Failed to log workout');
    } finally {
      setSavingWorkout(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const res = await createFighterSubscriptionCheckout();
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const handleLogWeighIn = async () => {
    if (!weighInWeight || isNaN(parseFloat(weighInWeight))) {
      toast.error('Please enter a valid weight');
      return;
    }
    setSavingWeighIn(true);
    try {
      await logFightWeekWeighIn({
        fight_id: fightWeekData.fight.id,
        weight: parseFloat(weighInWeight)
      });
      toast.success('Weight logged!');
      setShowWeighInDialog(false);
      setWeighInWeight('');
      loadData();
    } catch (error) {
      toast.error('Failed to log weight');
    } finally {
      setSavingWeighIn(false);
    }
  };

  const handleChecklistToggle = async (key) => {
    if (!fightWeekData?.fight?.id) return;
    const newChecklist = {
      ...fightWeekData.checklist,
      [key]: !fightWeekData.checklist[key]
    };
    
    // Optimistic update
    setFightWeekData(prev => ({
      ...prev,
      checklist: newChecklist
    }));
    
    try {
      await updateFightWeekChecklist(fightWeekData.fight.id, newChecklist);
    } catch (error) {
      // Revert on error
      setFightWeekData(prev => ({
        ...prev,
        checklist: fightWeekData.checklist
      }));
      toast.error('Failed to update checklist');
    }
  };

  const handleGoalStatusUpdate = async (goal, newStatus) => {
    // Optimistic update
    setCampGoalsData(prev => ({
      ...prev,
      active_goals: prev.active_goals.map(g => 
        g.id === goal.id ? { ...g, status: newStatus } : g
      )
    }));
    
    try {
      await updateCampGoal(goal.camp_id, goal.id, { status: newStatus });
      toast.success(newStatus === 'achieved' ? 'Goal achieved!' : 'Goal updated');
    } catch (error) {
      // Revert on error
      setCampGoalsData(prev => ({
        ...prev,
        active_goals: prev.active_goals.map(g => 
          g.id === goal.id ? { ...g, status: goal.status } : g
        )
      }));
      toast.error('Failed to update goal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { linked, fighter, camps, assessments, upcoming_fights, notes, gym_history } = dashboardData || {};
  const trialDaysLeft = subscriptionStatus?.trial_end_date 
    ? Math.max(0, safeDaysUntil(subscriptionStatus.trial_end_date))
    : 0;
  const isTrialing = subscriptionStatus?.subscription_status === 'trial';

  // Get latest data
  const activeCamp = camps?.find(c => c.status === 'active');
  const nextFight = upcoming_fights?.[0];
  const latestAssessment = assessments?.[0];
  const coachNotes = notes?.filter(n => n.author_role !== 'fighter').slice(0, 3) || [];

  // Not linked to a gym - show join flow
  if (!linked) {
    return (
      <div className="min-h-screen bg-black">
        <header className="border-b border-zinc-800 bg-zinc-950">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-zinc-500 text-sm">Fighter</p>
              </div>
            </div>
            <Button variant="ghost" onClick={logout} size="sm" className="text-zinc-400">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-zinc-900 border border-zinc-800 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-zinc-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Find Your Gym</h1>
          <p className="text-zinc-400 mb-8">Search for your gym to connect with your coaches</p>
          
          <div className="flex gap-2 mb-6">
            <Input
              value={gymSearch}
              onChange={(e) => setGymSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchGyms()}
              placeholder="Search gym name..."
              className="bg-zinc-900 border-zinc-800"
            />
            <Button onClick={handleSearchGyms} disabled={searchingGyms} className="bg-red-600 hover:bg-red-700">
              {searchingGyms ? '...' : 'Search'}
            </Button>
          </div>

          {gymsResults.length > 0 && (
            <div className="space-y-2 text-left">
              {gymsResults.map((gym) => (
                <button
                  key={gym.id}
                  onClick={() => setSelectedGym(gym)}
                  className={cn(
                    "w-full p-4 rounded-lg border text-left transition-colors",
                    selectedGym?.id === gym.id
                      ? "bg-red-600/10 border-red-600"
                      : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  )}
                >
                  <p className="text-white font-medium">{gym.name}</p>
                </button>
              ))}
            </div>
          )}

          {selectedGym && (
            <div className="mt-6 space-y-4">
              <Textarea
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                placeholder="Add a message (optional)"
                className="bg-zinc-900 border-zinc-800"
              />
              <Button 
                onClick={handleRequestJoinGym} 
                disabled={sendingRequest}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {sendingRequest ? 'Sending...' : `Request to Join ${selectedGym.name}`}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="fighter-portal">
      {/* Simple Header */}
      <header className="border-b border-zinc-800 bg-zinc-950 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {fighter?.name?.charAt(0) || 'F'}
            </div>
            <div>
              <p className="text-white font-semibold">{fighter?.name}</p>
              <p className="text-zinc-500 text-sm">{fighter?.weight_class || 'Fighter'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/fighter/profile">
              <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={logout} className="text-zinc-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Trial Banner */}
      {isTrialing && trialDaysLeft > 0 && (
        <div className="bg-gradient-to-r from-red-600/20 to-transparent border-b border-red-600/20 px-4 py-2">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <p className="text-sm text-red-400">
              <Crown className="w-4 h-4 inline mr-1" />
              {trialDaysLeft} days left on free trial
            </p>
            <Button size="sm" onClick={handleSubscribe} className="bg-red-600 hover:bg-red-700 h-7 text-xs">
              Subscribe
            </Button>
          </div>
        </div>
      )}

      {/* Main Content - Single scroll */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* FIGHT WEEK MODE */}
        <AnimatePresence>
          {fightWeekData?.in_fight_week && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
              data-testid="fight-week-mode"
            >
              {/* Fight Week Banner */}
              <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 rounded-xl p-1">
                <div className="bg-black rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Flame className="w-6 h-6 text-orange-500 animate-pulse" />
                      <span className="text-orange-500 font-bold uppercase tracking-wider">Fight Week</span>
                    </div>
                    <div className="text-right">
                      <span className="text-4xl font-bold text-white">{fightWeekData.fight.days_until}</span>
                      <span className="text-zinc-400 text-sm ml-1">days</span>
                    </div>
                  </div>
                  <p className="text-white text-xl font-semibold">vs {fightWeekData.fight.opponent_name}</p>
                  <p className="text-zinc-400">{fightWeekData.fight.event_name} • {safeDateFormat(fightWeekData.fight.fight_date, 'EEEE, M/d/yyyy')}</p>
                </div>
              </div>

              {/* Weight Cut Tracker */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                    <Scale className="w-4 h-4" />
                    WEIGHT CUT
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => setShowWeighInDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 h-8"
                    data-testid="log-weight-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Log Weight
                  </Button>
                </div>
                
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-zinc-500 text-xs uppercase">Current</p>
                    <p className="text-3xl font-bold text-white">
                      {fightWeekData.latest_weight || '--'} <span className="text-lg text-zinc-500">lbs</span>
                    </p>
                  </div>
                  {fightWeekData.target_weight && (
                    <div className="text-right">
                      <p className="text-zinc-500 text-xs uppercase">Target</p>
                      <p className="text-2xl font-bold text-green-500">
                        {fightWeekData.target_weight} <span className="text-lg text-zinc-500">lbs</span>
                      </p>
                    </div>
                  )}
                </div>
                
                {fightWeekData.target_weight && fightWeekData.latest_weight && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Progress</span>
                      <span className={cn(
                        "font-medium",
                        fightWeekData.latest_weight <= fightWeekData.target_weight 
                          ? "text-green-500" 
                          : "text-orange-500"
                      )}>
                        {fightWeekData.latest_weight <= fightWeekData.target_weight 
                          ? "On Weight!" 
                          : `${(fightWeekData.latest_weight - fightWeekData.target_weight).toFixed(1)} lbs to go`
                        }
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={cn(
                          "h-full transition-all duration-500",
                          fightWeekData.latest_weight <= fightWeekData.target_weight 
                            ? "bg-green-500" 
                            : "bg-orange-500"
                        )}
                        style={{ 
                          width: `${Math.min(100, Math.max(0, 
                            100 - ((fightWeekData.latest_weight - fightWeekData.target_weight) / fightWeekData.target_weight * 100)
                          ))}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {fightWeekData.weigh_in_countdown !== null && (
                  <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-2">
                    <Timer className="w-4 h-4 text-yellow-500" />
                    <span className="text-zinc-400 text-sm">Official weigh-in in</span>
                    <span className="text-white font-bold">{fightWeekData.weigh_in_countdown} days</span>
                  </div>
                )}
              </div>

              {/* Pre-Fight Checklist */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center gap-2 text-purple-400 text-sm font-medium mb-4">
                  <CheckCircle2 className="w-4 h-4" />
                  PRE-FIGHT CHECKLIST
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'medicals_complete', label: 'Medicals Complete' },
                    { key: 'fight_gear_packed', label: 'Fight Gear Packed' },
                    { key: 'travel_booked', label: 'Travel Booked' },
                    { key: 'hotel_confirmed', label: 'Hotel Confirmed' },
                    { key: 'corner_team_confirmed', label: 'Corner Team Ready' },
                    { key: 'weight_on_track', label: 'Weight On Track' },
                    { key: 'game_plan_reviewed', label: 'Game Plan Reviewed' },
                    { key: 'opponent_footage_watched', label: 'Film Study Done' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => handleChecklistToggle(item.key)}
                      className={cn(
                        "flex items-center gap-2 p-3 rounded-lg border transition-all text-left",
                        fightWeekData.checklist?.[item.key]
                          ? "bg-green-900/20 border-green-700/50 text-green-400"
                          : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                      )}
                      data-testid={`checklist-${item.key}`}
                    >
                      {fightWeekData.checklist?.[item.key] ? (
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <Circle className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span className="text-sm">{item.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Checklist Progress */}
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Checklist Progress</span>
                    <span className="text-white font-medium">
                      {Object.values(fightWeekData.checklist || {}).filter(Boolean).length} / 8
                    </span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 transition-all duration-300"
                      style={{ 
                        width: `${(Object.values(fightWeekData.checklist || {}).filter(Boolean).length / 8) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-px bg-zinc-800" />
                <span className="text-zinc-600 text-xs uppercase">Regular Dashboard</span>
                <div className="flex-1 h-px bg-zinc-800" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Record Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-center gap-6 py-4"
        >
          <div className="text-center">
            <p className="text-3xl font-bold text-white">
              {fighter?.record_wins || 0}-{fighter?.record_losses || 0}-{fighter?.record_draws || 0}
            </p>
            <p className="text-zinc-500 text-sm uppercase tracking-wider">Record</p>
          </div>
        </motion.div>

        {/* Next Fight - Hero Card */}
        {nextFight && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-red-600/20 to-red-900/10 border border-red-600/30 rounded-xl p-6"
            data-testid="next-fight-card"
          >
            <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-3">
              <Swords className="w-4 h-4" />
              NEXT FIGHT
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white mb-1">vs {nextFight.opponent_name}</p>
                <p className="text-zinc-400">{nextFight.event_name}</p>
                <p className="text-zinc-500 text-sm mt-1">{safeDateFormat(nextFight.fight_date, 'EEEE, M/d/yyyy')}</p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-red-500">{safeDaysUntil(nextFight.fight_date)}</p>
                <p className="text-zinc-500 text-sm">days</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Current Camp */}
        {activeCamp ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
            data-testid="current-camp-card"
          >
            <div className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-3">
              <Target className="w-4 h-4" />
              CURRENT CAMP
            </div>
            <p className="text-xl font-semibold text-white mb-1">{activeCamp.name}</p>
            <p className="text-zinc-400 text-sm">{activeCamp.description}</p>
            <div className="flex items-center gap-4 mt-3 text-zinc-500 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {safeDateFormat(activeCamp.start_date, 'M/d')} - {safeDateFormat(activeCamp.end_date, 'M/d')}
              </span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-6 text-center"
          >
            <Target className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-500">No active training camp</p>
          </motion.div>
        )}

        {/* Camp Goals - Shows when there's an active camp */}
        {campGoalsData?.active_goals?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
            data-testid="camp-goals-section"
          >
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-white font-medium">Camp Goals</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-400">
                  {campGoalsData.stats?.achieved || 0}/{campGoalsData.stats?.total || 0} achieved
                </span>
                <div className="w-20 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 transition-all"
                    style={{ width: `${campGoalsData.stats?.completion_rate || 0}%` }}
                  />
                </div>
              </div>
            </div>
            
            <div className="divide-y divide-zinc-800 max-h-64 overflow-y-auto">
              {campGoalsData.active_goals.slice(0, 5).map((goal) => (
                <div key={goal.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                      onClick={() => handleGoalStatusUpdate(
                        goal, 
                        goal.status === 'achieved' ? 'in_progress' : 'achieved'
                      )}
                      className={cn(
                        "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                        goal.status === 'achieved' 
                          ? "bg-green-600 border-green-500" 
                          : goal.status === 'in_progress'
                            ? "border-yellow-500 bg-yellow-500/20"
                            : "border-zinc-600 hover:border-zinc-500"
                      )}
                      data-testid={`goal-toggle-${goal.id}`}
                    >
                      {goal.status === 'achieved' && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                      {goal.status === 'in_progress' && (
                        <div className="w-2 h-2 rounded-full bg-yellow-500" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm font-medium truncate",
                        goal.status === 'achieved' ? "text-zinc-500 line-through" : "text-white"
                      )}>
                        {goal.title}
                      </p>
                      {goal.target_metric && (
                        <p className="text-xs text-zinc-500">Target: {goal.target_metric}</p>
                      )}
                    </div>
                  </div>
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-sm ml-2 flex-shrink-0",
                    goal.goal_type === 'skill' ? "bg-blue-600/20 text-blue-400" :
                    goal.goal_type === 'physical' ? "bg-green-600/20 text-green-400" :
                    goal.goal_type === 'technical' ? "bg-purple-600/20 text-purple-400" :
                    goal.goal_type === 'weight' ? "bg-orange-600/20 text-orange-400" :
                    "bg-zinc-700 text-zinc-400"
                  )}>
                    {goal.goal_type}
                  </span>
                </div>
              ))}
            </div>
            
            {campGoalsData.active_goals.length > 5 && (
              <div className="px-5 py-2 bg-zinc-900/50 border-t border-zinc-800 text-center">
                <span className="text-zinc-500 text-xs">+{campGoalsData.active_goals.length - 5} more goals</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Quick Log Workout Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button 
            onClick={() => setShowLogWorkout(true)}
            className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 h-14 text-base"
            data-testid="log-workout-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Log Today's Workout
          </Button>
        </motion.div>

        {/* Belt Tracking (for BJJ/Hybrid gyms) */}
        {fighter?.bjj_belt && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            data-testid="belt-tracking-card"
          >
            <BeltTrackingCard fighter={fighter} isCoach={false} />
          </motion.div>
        )}

        {/* Latest Assessment */}
        {latestAssessment && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
            data-testid="latest-assessment-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <TrendingUp className="w-4 h-4" />
                LATEST ASSESSMENT
              </div>
              <span className="text-zinc-500 text-xs">{safeDateFormat(latestAssessment.date, 'M/d/yyyy')}</span>
            </div>
            
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{latestAssessment.striking}</p>
                <p className="text-zinc-500 text-xs">Striking</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{latestAssessment.grappling}</p>
                <p className="text-zinc-500 text-xs">Grappling</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{latestAssessment.wrestling}</p>
                <p className="text-zinc-500 text-xs">Wrestling</p>
              </div>
              <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{latestAssessment.cardio}</p>
                <p className="text-zinc-500 text-xs">Cardio</p>
              </div>
            </div>
            
            {latestAssessment.notes && (
              <p className="text-zinc-400 text-sm italic border-t border-zinc-800 pt-3">
                "{latestAssessment.notes}"
              </p>
            )}
          </motion.div>
        )}

        {/* Coach Notes */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
          data-testid="coach-notes-section"
        >
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium">
              <MessageSquare className="w-4 h-4" />
              COACH NOTES
            </div>
            <Link to="/fighter/notes" className="text-zinc-500 text-sm hover:text-white transition-colors">
              View All
              <ChevronRight className="w-4 h-4 inline" />
            </Link>
          </div>
          
          {coachNotes.length > 0 ? (
            <div className="divide-y divide-zinc-800">
              {coachNotes.map((note) => (
                <div key={note.id} className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white text-sm font-medium">{note.author_name}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500 text-xs">{safeDateFormat(note.created_at, 'M/d')}</span>
                  </div>
                  <p className="text-zinc-300 text-sm">{note.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-8 text-center text-zinc-500">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No notes from coaches yet</p>
            </div>
          )}
          
          <div className="px-5 py-3 bg-zinc-900/50 border-t border-zinc-800">
            <Link to="/fighter/notes">
              <Button variant="ghost" className="w-full text-zinc-400 hover:text-white">
                <Send className="w-4 h-4 mr-2" />
                Message Coach
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="grid grid-cols-3 gap-3"
        >
          <Link 
            to="/fighter/profile"
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
          >
            <User className="w-5 h-5 text-zinc-400 mb-2" />
            <p className="text-white text-sm font-medium">My Profile</p>
            <p className="text-zinc-500 text-xs">Edit info & socials</p>
          </Link>
          
          <Link 
            to="/fighter/analysis"
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
          >
            <TrendingUp className="w-5 h-5 text-zinc-400 mb-2" />
            <p className="text-white text-sm font-medium">Fight Analysis</p>
            <p className="text-zinc-500 text-xs">Review past fights</p>
          </Link>
          
          <button 
            onClick={() => setShowJoinGymDialog(true)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors text-left"
          >
            <Building2 className="w-5 h-5 text-zinc-400 mb-2" />
            <p className="text-white text-sm font-medium">Gym History</p>
            <p className="text-zinc-500 text-xs">{gym_history?.length || 0} gyms</p>
          </button>
        </motion.div>

        {/* Gym History Preview */}
        {gym_history && gym_history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-5"
            data-testid="gym-history-preview"
          >
            <p className="text-zinc-400 text-sm font-medium mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              TRAINING JOURNEY
            </p>
            <div className="space-y-2">
              {gym_history.slice(0, 3).map((entry, idx) => (
                <div key={entry.id} className="flex items-center gap-3">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    entry.status === 'active' ? "bg-green-500" : "bg-zinc-600"
                  )} />
                  <span className="text-white text-sm">{entry.gym_name}</span>
                  <span className="text-zinc-600 text-xs ml-auto">{entry.duration}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

      </main>

      {/* Log Workout Dialog */}
      <Dialog open={showLogWorkout} onOpenChange={setShowLogWorkout}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-red-500" />
              Log Workout
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">What did you work on?</label>
              <Input
                value={workoutData.focus}
                onChange={(e) => setWorkoutData(prev => ({ ...prev, focus: e.target.value }))}
                placeholder="e.g., Sparring, Pad work, Wrestling drills"
                className="bg-zinc-950 border-zinc-800"
                data-testid="workout-focus-input"
              />
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">Coach (optional)</label>
              <Input
                value={workoutData.coach}
                onChange={(e) => setWorkoutData(prev => ({ ...prev, coach: e.target.value }))}
                placeholder="Coach name"
                className="bg-zinc-950 border-zinc-800"
                data-testid="workout-coach-input"
              />
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">Duration</label>
              <Select 
                value={workoutData.duration} 
                onValueChange={(val) => setWorkoutData(prev => ({ ...prev, duration: val }))}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="workout-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3+ hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLogWorkout(false)}>Cancel</Button>
            <Button 
              onClick={handleLogWorkout} 
              disabled={savingWorkout}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-workout-btn"
            >
              {savingWorkout ? 'Saving...' : 'Save Workout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gym History Dialog */}
      <Dialog open={showJoinGymDialog} onOpenChange={setShowJoinGymDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              Training History
            </DialogTitle>
          </DialogHeader>
          
          {gym_history && gym_history.length > 0 ? (
            <div className="space-y-3 py-4">
              {gym_history.map((entry, idx) => (
                <div 
                  key={entry.id}
                  className={cn(
                    "p-4 rounded-lg border",
                    entry.status === 'active' 
                      ? "bg-green-900/20 border-green-700/50" 
                      : "bg-zinc-800/50 border-zinc-700"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-medium">{entry.gym_name}</span>
                    {entry.status === 'active' && (
                      <span className="text-xs px-2 py-0.5 bg-green-600/30 text-green-400 rounded">Current</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">
                      {safeDateFormat(entry.joined_at, 'MMM yyyy')}
                      {entry.left_at ? ` - ${safeDateFormat(entry.left_at, 'MMM yyyy')}` : ' - Present'}
                    </span>
                    <span className="text-zinc-500">{entry.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-zinc-500">
              <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No gym history yet</p>
            </div>
          )}
          
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-zinc-400 text-sm mb-3">Want to join a different gym?</p>
            <div className="flex gap-2">
              <Input
                value={gymSearch}
                onChange={(e) => setGymSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchGyms()}
                placeholder="Search gym name..."
                className="bg-zinc-950 border-zinc-800"
              />
              <Button onClick={handleSearchGyms} disabled={searchingGyms} size="sm">
                Search
              </Button>
            </div>
            
            {gymsResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {gymsResults.map((gym) => (
                  <button
                    key={gym.id}
                    onClick={() => setSelectedGym(gym)}
                    className={cn(
                      "w-full p-3 rounded-lg border text-left transition-colors",
                      selectedGym?.id === gym.id
                        ? "bg-red-600/10 border-red-600"
                        : "bg-zinc-800 border-zinc-700 hover:border-zinc-600"
                    )}
                  >
                    <p className="text-white text-sm">{gym.name}</p>
                  </button>
                ))}
              </div>
            )}
            
            {selectedGym && (
              <div className="mt-3 space-y-3">
                <Textarea
                  value={joinMessage}
                  onChange={(e) => setJoinMessage(e.target.value)}
                  placeholder="Message to gym (optional)"
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                />
                <Button 
                  onClick={handleRequestJoinGym}
                  disabled={sendingRequest}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {sendingRequest ? 'Sending...' : 'Request to Join'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Weigh-In Dialog */}
      <Dialog open={showWeighInDialog} onOpenChange={setShowWeighInDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-500" />
              Log Weight
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-zinc-400 text-sm mb-2 block">Current Weight (lbs)</label>
            <Input
              type="number"
              step="0.1"
              value={weighInWeight}
              onChange={(e) => setWeighInWeight(e.target.value)}
              placeholder="e.g., 185.5"
              className="bg-zinc-950 border-zinc-800 text-2xl text-center h-14"
              data-testid="weigh-in-input"
            />
            {fightWeekData?.target_weight && weighInWeight && (
              <p className={cn(
                "mt-2 text-center text-sm",
                parseFloat(weighInWeight) <= fightWeekData.target_weight
                  ? "text-green-500"
                  : "text-orange-500"
              )}>
                {parseFloat(weighInWeight) <= fightWeekData.target_weight
                  ? "On weight!"
                  : `${(parseFloat(weighInWeight) - fightWeekData.target_weight).toFixed(1)} lbs over target`
                }
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowWeighInDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleLogWeighIn}
              disabled={savingWeighIn || !weighInWeight}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-weigh-in-btn"
            >
              {savingWeighIn ? 'Saving...' : 'Log Weight'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FighterDashboard;
